import { useEffect } from 'react';
import { TextField, Button, Stack, MenuItem, Typography, Divider, useTheme, styled } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TagsInput } from '../TagsInput';
import { EmailsInput } from '../EmailsInput/EmailsInput';
import { useInviteUser } from 'hooks/useInviteUser';
import { useAuthContext } from 'hooks/useAuthContext';
import { UserRoles } from 'constants/userRoles';
import { useGetInvitationRoles } from 'hooks/useGetInvitationRoles';
import { LanguageSelect, RoleSelect, StyledDivider, TabDialogActions, TabDialogContent } from '../InviteUserDialog/InviteUserDialog.styles';
import { createColorFilteredComponent } from 'helpers/styledHelpers';

const StyledMenuItem = createColorFilteredComponent<{ $color: string }>(MenuItem)(({ $color }) => ({
  color: $color
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12
  }
});

type InviteUsersFormValues = {
  emails: string[];
  invalidEmailsExist: boolean;
  tags: string[];
  message: string;
  roleId: string;
  culture: string;
};

const createInviteUsersSchema = () => z.object({
  emails: z.array(z.string()).min(1),
  invalidEmailsExist: z.boolean().refine((value) => !value),
  tags: z.array(z.string()).max(2),
  message: z.string(),
  roleId: z.string(),
  culture: z.string()
});

interface InviteUsersTabProps {
  onClose: () => void;
}

export const InviteUsersTab: React.FC<InviteUsersTabProps> = ({ onClose }) => {
  const { userState } = useAuthContext();
  const { invitationRoles } = useGetInvitationRoles();
  const { inviteUser, invitingUser } = useInviteUser();
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { control, handleSubmit, setValue, formState: { isValid } } = useForm<InviteUsersFormValues>({
    resolver: zodResolver(createInviteUsersSchema()),
    mode: 'onChange',
    defaultValues: {
      emails: [],
      invalidEmailsExist: false,
      tags: [],
      message: '',
      roleId: '',
      culture: i18n.language
    }
  });

  const handleEmailsChange = (emails: string[], invalidEmailsExist: boolean) => {
    setValue('emails', emails, { shouldValidate: true });
    setValue('invalidEmailsExist', invalidEmailsExist, { shouldValidate: true });
  };

  const onSubmit = async (values: InviteUsersFormValues) => {
    const [tagA = '', tagB = ''] = values.tags;
    const resp = await inviteUser({
      emails: values.emails,
      tagA,
      tagB,
      message: values.message,
      culture: values.culture,
      roleId: values.roleId
    });

    if (resp?.success) {
      onClose();
    }
  };

  useEffect(() => {
    if (invitationRoles) {
      setValue('roleId', invitationRoles.find(role => role.name === UserRoles.Participant)?.id || '', { shouldValidate: true });
    }
  }, [invitationRoles, setValue]);

  useEffect(() => {
    setValue('culture', i18n.language, { shouldValidate: true });
  }, [i18n.language, setValue]);

  return (
    <>
      <TabDialogContent>
          <Stack direction={'column'} paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} paddingBottom={theme.spacing(5)} spacing={theme.spacing(3)}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Stack>
                <Typography variant='h5' fontWeight={'600'}>
                  {t('inviteUser.inviteUserTitle')}
                </Typography>
                <Typography variant='subtitle1' color={theme.palette.text.secondary}>
                  {t('inviteUser.inviteUserSubtitle', { organizationName: userState.user.selectedWorkspace.workspaceName })}
                </Typography>
              </Stack>
              <Stack direction={'row'} alignItems={'center'}>
                <Typography variant='subtitle1' fontWeight={500}>
                  {t('inviteUser.invitationLanguage')}:
                </Typography>
                <Controller
                  name='culture'
                  control={control}
                  render={({ field }) => (
                    <LanguageSelect
                      labelId="invitation-languages"
                      id="invitation-languages"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value as string)}
                      variant="outlined"
                      IconComponent={KeyboardArrowDownIcon}
                    >
                      <StyledMenuItem value="en" $color={theme.palette.black.main}>EN</StyledMenuItem>
                      <StyledMenuItem value="pl" $color={theme.palette.black.main}>PL</StyledMenuItem>
                    </LanguageSelect>
                  )}
                />
              </Stack>
            </Stack>

            <Typography variant='subtitle2'>{t('inviteUser.enterAdresses')}</Typography>
            <EmailsInput onChange={handleEmailsChange} />
            <Divider />
            <Stack display='flex' flexDirection={'row'}>
              <Stack flexDirection={'column'} display={'flex'} height='100%' width='50%' paddingTop={theme.spacing(2)} spacing={theme.spacing(2)}>
                <Typography variant='subtitle2'>{t('inviteUserDialog.selectUserFunction')}</Typography>
                {invitationRoles && (
                  <Controller
                    name='roleId'
                    control={control}
                    render={({ field }) => (
                      <RoleSelect
                        labelId="invitation-roles-label"
                        id="invitation-roles-label"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value as string)}
                        variant='outlined'
                      >
                        {invitationRoles.map((role, index) => (
                          <MenuItem key={index} value={role.id}>
                            <Typography fontWeight={'500'}>{t(`inviteUserDialog.roles.${role.name}`)}</Typography>
                          </MenuItem>
                        ))}
                      </RoleSelect>
                    )}
                  />
                )}
              </Stack>
              <Divider orientation='vertical' />
              <Stack flexDirection={'column'} display={'flex'} height='100%' width='50%' paddingTop={theme.spacing(2)} paddingLeft={theme.spacing(4)} spacing={theme.spacing(2)}>
                <Typography variant='subtitle2'>{t('inviteUserDialog.addTags')}</Typography>
                <TagsInput onChange={(tags) => setValue('tags', tags, { shouldValidate: true })} />
              </Stack>
            </Stack>
            <StyledDivider />
            <Typography variant='subtitle2'>{t('inviteUser.addMessage')}</Typography>
            <Controller
              name='message'
              control={control}
              render={({ field }) => (
                <StyledTextField
                  label={t('inviteUser.messageContent')}
                  value={field.value}
                  onChange={field.onChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              )}
            />
          </Stack>
      </TabDialogContent>
      <TabDialogActions>
        <Stack paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} direction='row' spacing={theme.spacing(3)}>
          <Button onClick={onClose} variant='outlined'>{t('general.cancel')}</Button>
          <LoadingButton
            data-testid="invite-user-send-invite-button"
            loading={invitingUser}
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary">
            {t('inviteUser.sendInvite')}
          </LoadingButton>
        </Stack>
      </TabDialogActions>
    </>
  );
};
